import { api } from "./client";

/** The caller's Slack link (GET /me/slack-link). */
export interface SlackLinkStatus {
  /** Is the Slack app installed for the caller's organization? */
  connected: boolean;
  slackTeamName: string | null;
  link: { slackUserId: string; linkedAt: string } | null;
}

export interface SlackLinkResult {
  slackTeamName: string;
  slackUserId: string;
}

/** POST /me/slack-link/start: open the app's DM in Slack and run /compbox. */
export interface SlackLinkStart {
  /** slack:// deep link to the CompBox app's DM. */
  url: string;
  /** Fallback that opens the workspace in a browser. */
  webUrl: string;
  slackTeamName: string;
  instructions: string;
}

export interface SlackChannel {
  id: string;
  name: string;
}

/** The organization's Slack app (GET /integrations/slack). */
export interface SlackIntegrationStatus {
  /** Does this CompBox server have a Slack app configured at all? */
  configured: boolean;
  /** Is the caller an organization admin (may connect/disconnect/pick a channel)? */
  canManage: boolean;
  installation: {
    slackTeamId: string;
    slackTeamName: string;
    scopes: string[];
    installedAt: string;
    /** Public channel for org-wide build finished/failed updates. */
    notifyChannel: SlackChannel | null;
  } | null;
}

/** Event kinds a linked user can toggle; mirrors the API's SLACK_NOTIFICATION_KINDS. */
export const SLACK_NOTIFICATION_KINDS = [
  "needs_input",
  "merge_approval",
  "run_finished",
  "run_failed",
  "run_stopped",
] as const;
export type SlackNotificationKind = (typeof SLACK_NOTIFICATION_KINDS)[number];

export interface SlackQuietHours {
  /** "HH:MM", 24-hour. */
  start: string;
  end: string;
  /** IANA time zone. */
  tz: string;
}

/** A linked user's notification preferences (GET/PATCH /me/slack-preferences). */
export interface SlackPreferences {
  events: Record<SlackNotificationKind, boolean>;
  quietHours: SlackQuietHours | null;
}

export interface UpdateSlackPreferences {
  events?: Partial<Record<SlackNotificationKind, boolean>>;
  quietHours?: SlackQuietHours | null;
}

export const slackApi = {
  getLinkStatus: () => api.get<{ data: SlackLinkStatus }>("/me/slack-link"),
  /** Redeem a one-time link token for the signed-in user. */
  confirmLink: (token: string) =>
    api.post<{ data: SlackLinkResult }>("/me/slack-link/confirm", { token }),
  /** A deep link into the workspace where the CompBox app DMs a link token. */
  startLink: () =>
    api.post<{ data: SlackLinkStart }>("/me/slack-link/start", {}),
  unlink: () => api.delete<{ data: unknown }>("/me/slack-link"),

  status: () => api.get<{ data: SlackIntegrationStatus }>("/integrations/slack"),
  /** (org admin) the "Add to Slack" authorize URL. */
  installUrl: () => api.get<{ data: { url: string } }>("/integrations/slack/install"),
  /** (org admin) revoke the bot token and disconnect. */
  disconnect: () => api.delete<undefined>("/integrations/slack"),
  /** (org admin) public channels for the notify-channel picker. */
  channels: () =>
    api.get<{ data: { channels: SlackChannel[]; truncated: boolean } }>("/integrations/slack/channels"),
  /** (org admin) set or clear the org build-updates channel. */
  setNotifyChannel: (channelId: string | null) =>
    api.patch<{ data: { notifyChannel: SlackChannel | null } }>("/integrations/slack/notify-channel", {
      channelId,
    }),

  getPreferences: () => api.get<{ data: SlackPreferences }>("/me/slack-preferences"),
  updatePreferences: (input: UpdateSlackPreferences) =>
    api.patch<{ data: SlackPreferences }>("/me/slack-preferences", input),
};
